// Vendor categories, subcategories, and category-specific field definitions.
// Keep server-side `VendorCategory` in server.py aligned with `key` values here.

export const CATEGORIES = [
  { key: "catering",       label: "Catering",             tag: "01", short: "Caterers" },
  { key: "restaurant",     label: "Restaurants",          tag: "02", short: "Restaurants" },
  { key: "venue",          label: "Venues",               tag: "03", short: "Venues" },
  { key: "florist",        label: "Florists",             tag: "04", short: "Florists" },
  { key: "dj",             label: "DJs",                  tag: "05", short: "DJs" },
  { key: "rentals",        label: "Rentals",              tag: "06", short: "Rentals" },
  { key: "av",             label: "A/V",                  tag: "07", short: "A/V" },
  { key: "security",       label: "Security",             tag: "08", short: "Security" },
  { key: "photography",    label: "Photography",          tag: "09", short: "Photography" },
  { key: "transportation", label: "Transportation & Limos", tag: "10", short: "Transport" },
];

// Canonical `key → human label` map. Import everywhere the vendor's category
// is shown to a user so we never render raw keys like "dj".
export const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.key, c.label]));

// Categories where a food-oriented "Menu" surface makes sense. Every other
// category still gets the generic Services field on the listing edit form.
export const FOOD_CATEGORIES = new Set(["catering", "restaurant"]);

// Every category the AI Packages & Services importer applies to. Food
// categories see menu terminology; the rest see "Packages & Services".
export const OFFERING_CATEGORIES = new Set([
  "catering", "restaurant", "venue", "florist", "dj", "rentals", "av",
  "photography", "transportation", "security",
]);

export function offeringsTabLabel(category) {
  return FOOD_CATEGORIES.has(category) ? "Menus, Packages & Services" : "Packages & Services";
}

// Presentation-only terminology for the manual offerings builder and the
// public profile. The underlying Menu → Category → Item data model and API
// routes are unchanged; only visible copy differs per vendor category.
const DEFAULT_OFFERING_LABELS = {
  builderTitle: "Build a menu",
  builderHint: "",
  menuNoun: "Menu",
  menuPlaceholder: "Menu name (e.g. Dinner)",
  addMenu: "Menu",
  categoryPlaceholder: "Category (e.g. Appetizers)",
  addCategory: "+ Category",
  addItem: "+ Item",
  addItemTitle: (categoryName) => `Add item to ${categoryName}`,
  addItemButton: "Add item",
  emptyText: "No menus yet. Create one above to start adding categories and items.",
  deleteMenu: "Delete menu",
  menuCreated: "Menu created",
  categoryAdded: "Category added",
  itemAdded: "Item added",
  itemUpdated: "Item updated",
  publicEyebrow: "Menus & services",
  quotePickerLabel: "Interested menu items",
};

const DJ_OFFERING_LABELS = {
  builderTitle: "Build your DJ services",
  builderHint: "Organize your services into sections such as Packages, Hourly Services and Add-ons.",
  menuNoun: "Service collection",
  menuPlaceholder: "e.g. Weddings or Corporate Events",
  addMenu: "Service Collection",
  categoryPlaceholder: "e.g. Packages, Hourly Services or Add-ons",
  addCategory: "+ Section",
  addItem: "+ Package or Service",
  addItemTitle: (categoryName) => `Add a package or service to ${categoryName}`,
  addItemButton: "Add Package or Service",
  emptyText: "Create a service collection, then add sections and packages.",
  deleteMenu: "Delete collection",
  menuCreated: "Service collection created",
  categoryAdded: "Section added",
  itemAdded: "Package or service added",
  itemUpdated: "Package or service updated",
  publicEyebrow: "DJ Packages & Services",
  quotePickerLabel: "Interested packages or services",
};

export function offeringLabels(category) {
  return category === "dj" ? DJ_OFFERING_LABELS : DEFAULT_OFFERING_LABELS;
}

// Human-readable subcategory (specialty) options — used in vendor onboarding, dashboard,
// and as customer filter chips on the Browse page.
export const SUBCATEGORIES = {
  photography: [
    "Event Photographer", "Wedding Photographer", "Portrait Photographer",
    "Corporate Photographer", "Videographer", "Drone Photography",
    "Photo Booth", "360 Photo Booth", "Content Creator", "Livestream Services", "Other",
  ],
  transportation: [
    "Limousine Service", "Luxury Sedan", "Executive SUV", "Party Bus",
    "Shuttle Service", "Charter Bus", "Wedding Transportation",
    "Airport Transportation", "Guest Shuttle", "Vintage Vehicle",
    "Exotic Car Rental", "Chauffeur Service", "Other",
  ],
};

// Extra profile fields per category — {key, label, type, options?, placeholder?}
// The Vendor listing dialog renders these dynamically when the vendor's category matches.
export const CATEGORY_FIELDS = {
  photography: [
    { key: "subcategory",       label: "Specialty",           type: "select",   options: SUBCATEGORIES.photography },
    { key: "portfolio_url",     label: "Portfolio URL",       type: "url",      placeholder: "https://your-portfolio.com" },
    { key: "video_reel_url",    label: "Video reel URL",      type: "url",      placeholder: "https://youtube.com/…" },
    { key: "years_in_business", label: "Years in business",   type: "number",   min: 0, max: 100 },
    { key: "instagram",         label: "Instagram handle",    type: "text",     placeholder: "@yourstudio" },
  ],
  transportation: [
    { key: "subcategory",         label: "Specialty",              type: "select", options: SUBCATEGORIES.transportation },
    { key: "vehicle_types",       label: "Vehicle types",          type: "text",   placeholder: "Sedan, SUV, Sprinter, Coach…" },
    { key: "passenger_capacity",  label: "Max passengers",         type: "number", min: 1, max: 100 },
    { key: "fleet_size",          label: "Fleet size",             type: "number", min: 1, max: 500 },
    { key: "chauffeur_included",  label: "Chauffeur included?",    type: "select", options: ["Yes", "No", "Optional"] },
    { key: "minimum_rental_hours",label: "Minimum rental (hours)", type: "number", min: 1, max: 24 },
    { key: "hourly_rate",         label: "Hourly rate ($)",        type: "number", min: 0 },
    { key: "mileage_charge",      label: "Mileage charge / mile",  type: "number", min: 0 },
    { key: "overtime_rate",       label: "Overtime rate ($/hr)",   type: "number", min: 0 },
  ],
};

// Extra filter chips exposed on Browse for a given category.
export const CATEGORY_FILTERS = {
  photography: [
    { key: "specialty", label: "Specialty", options: SUBCATEGORIES.photography },
  ],
  transportation: [
    { key: "specialty",          label: "Vehicle type",       options: SUBCATEGORIES.transportation },
    { key: "min_passenger_capacity", label: "Min passengers", options: [4, 8, 12, 20, 30, 50], suffix: " pax" },
  ],
};
